import React from 'react';
import {
  RailsBlock,
  RailsPageView,
} from '@sls-react/typings/services/rails/tile';
import dynamic from 'next/dynamic';
import BlockSection from '@sls-react/components/legos/BlockSection';
import getEnv from '@sls-react/utils/getEnv';
import { Message } from '@sls-react/design-system';
import FreestarAd from '@sls-react/components/FreestarAd';
import { useSession } from '@sls-react/contexts/SessionContext';
import TextPillsBlock from '@sls-react/components/legos/Blocks/TextPillsBlock';
import SingleBlock from '@sls-react/components/legos/Blocks/SingleBlock';
import TextTitleBlock from '@sls-react/components/legos/Blocks/TextTitleBlock';
import ItemGridHorizontalScrollBlock from '@sls-react/components/legos/Blocks/ItemGridHorizontalScrollBlock';
import ItemGridRowBlock from '@sls-react/components/legos/Blocks/ItemGridRowBlock';
import LanderHeaderBlock from '@sls-react/components/legos/Blocks/LanderHeaderBlock';
import ModelsBlock from '@sls-react/components/legos/Blocks/ModelsBlock';
import RecentlyViewedItemBlock from '@sls-react/components/legos/Blocks/RecentlyViewedItemBlock';
import CategorySelections from '@sls-react/components/legos/Blocks/CategorySelections';
import UnlimitedTitleTile from '@sls-react/components/legos/Tiles/UnlimitedTitleTile';
import UnlimitedTile from '@sls-react/components/legos/Tiles/UnlimitedTile';
import BasicTile from '@sls-react/components/legos/Tiles/BasicTile';

const BuyingGuideBlock = dynamic(
  () => import('@sls-react/components/legos/Blocks/BuyingGuideBlock'),
);
const TextParagraphBlock = dynamic(
  () => import('@sls-react/components/legos/Blocks/TextParagraphBlock'),
);
const HomepageCmsHero = dynamic(() => import('../Homepage/HomepageCmsHero'));
const ResultsGridBlock = dynamic(
  () => import('@sls-react/components/Search/ResultsGridBlock'),
);
const ShortHeroBlock = dynamic(
  () => import('@sls-react/components/legos/Blocks/ShortHeroBlock'),
);
const MicroBannerBlock = dynamic(
  () => import('@sls-react/components/legos/Blocks/MicroBannerBlock'),
);

interface Props {
  block: RailsBlock;
  hasHero: boolean;
  hasTitleBlock: boolean;
  pageView?: RailsPageView;
}

const RenderBlock: React.FC<Props> = ({
  block,
  hasHero,
  hasTitleBlock,
  pageView,
}) => {
  const session = useSession();

  if (!session.loggedIn && block.login_required) {
    if (getEnv() !== 'production') {
      return (
        <div className="container my-4">
          <Message info>
            Block &ldquo;
            <code>{block.title}</code>&rdquo; skipped because login is required
            and you&apos;re not logged in
          </Message>
        </div>
      );
    }
    return null;
  }

  let content;

  let sectionHeaderType: 'h1' | 'h2' | 'h3' | undefined = undefined;

  if (hasHero && hasTitleBlock) {
    sectionHeaderType = 'h3';
  } else if (hasHero && !hasTitleBlock) {
    sectionHeaderType = 'h2';
  } else if (!hasHero && !hasTitleBlock) {
    sectionHeaderType = 'h1';
  }

  switch (block.layout) {
    case 'carousel':
    case 'hero':
      content = <HomepageCmsHero block={block} />;
      break;

    case 'lander-header':
      content = (
        <LanderHeaderBlock block={block} headerType={hasHero ? 'h2' : 'h1'} />
      );
      break;

    case 'text-title':
      content = (
        <TextTitleBlock block={block} headerType={hasHero ? 'h2' : 'h1'} />
      );
      break;

    case 'text-paragraph':
      content = <TextParagraphBlock block={block} />;
      break;

    case 'group-rectangle':
    case 'group-8':
      content = (
        <BlockSection block={block} headerType={sectionHeaderType}>
          <div className="grid gap-2 grid-cols-2 md:gap-4 md:grid-cols-4">
            {block.tiles?.map(tile => (
              <BasicTile key={tile.id} tile={tile} />
            ))}
          </div>
        </BlockSection>
      );
      break;

    case 'group-square':
    case 'group-4':
      content = (
        <BlockSection block={block} headerType={sectionHeaderType}>
          <div className="grid gap-2 grid-cols-3 md:gap-4 md:grid-cols-6">
            {block.tiles?.map(tile => (
              <BasicTile key={tile.id} tile={tile} />
            ))}
          </div>
        </BlockSection>
      );
      break;

    case 'group-unlimited':
      content = (
        <BlockSection block={block} headerType={sectionHeaderType}>
          <div className="grid gap-2 grid-cols-3 md:gap-4">
            {block.tiles
              ?.sort((a, b) => a.title.localeCompare(b.title))
              .map(tile => (
                <UnlimitedTile key={tile.id} tile={tile} />
              ))}
          </div>
        </BlockSection>
      );
      break;

    case 'group-unlimited-title':
      content = (
        <BlockSection block={block} headerType={sectionHeaderType}>
          <div className="grid gap-2 grid-cols-3 md:gap-4">
            {block.tiles
              ?.sort((a, b) => a.title.localeCompare(b.title))
              .map(tile => (
                <a key={tile.id} href={tile.destination}>
                  <UnlimitedTitleTile title={tile.title} />
                </a>
              ))}
          </div>
        </BlockSection>
      );
      break;

    case 'group-unlimited-1-column-title':
      content = (
        <BlockSection block={block} headerType={sectionHeaderType}>
          <div className="grid gap-2 grid-cols-1 md:gap-4 md:grid-cols-3">
            {block.tiles
              ?.sort((a, b) => a.title.localeCompare(b.title))
              .map(tile => (
                <a key={tile.id} href={tile.destination}>
                  <UnlimitedTitleTile title={tile.title} />
                </a>
              ))}
          </div>
        </BlockSection>
      );
      break;

    case 'group-unlimited-1-column-title-image':
      content = (
        <BlockSection block={block} headerType={sectionHeaderType}>
          <div className="grid gap-2 grid-cols-1 md:gap-4 md:grid-cols-3">
            {block.tiles
              ?.sort((a, b) => a.title.localeCompare(b.title))
              .map(tile => (
                <UnlimitedTile key={tile.id} tile={tile} />
              ))}
          </div>
        </BlockSection>
      );
      break;

    case 'group-unlimited-2-column-title':
      content = (
        <BlockSection block={block} headerType={sectionHeaderType}>
          <div className="grid gap-2 grid-cols-2 md:gap-4 md:grid-cols-3">
            {block.tiles
              ?.sort((a, b) => a.title.localeCompare(b.title))
              .map(tile => (
                <a key={tile.id} href={tile.destination}>
                  <UnlimitedTitleTile title={tile.title} />
                </a>
              ))}
          </div>
        </BlockSection>
      );
      break;

    case 'group-unlimited-2-column-title-image':
      content = (
        <BlockSection block={block} headerType={sectionHeaderType}>
          <div className="grid gap-2 grid-cols-2 md:gap-4 md:grid-cols-3">
            {block.tiles
              ?.sort((a, b) => a.title.localeCompare(b.title))
              .map(tile => (
                <UnlimitedTile key={tile.id} tile={tile} />
              ))}
          </div>
        </BlockSection>
      );
      break;

    case 'single':
      content = <SingleBlock block={block} />;
      break;

    case 'micro-banner':
      if (!pageView) {
        break;
      }
      content = <MicroBannerBlock block={block} pageView={pageView} />;
      break;

    case 'item-grid-horizontal-scroll':
      content = (
        <BlockSection block={block} headerType={sectionHeaderType}>
          <ItemGridHorizontalScrollBlock block={block} />
        </BlockSection>
      );
      break;

    case 'item-grid-1-row':
      content = (
        <BlockSection block={block} headerType={sectionHeaderType}>
          <ItemGridRowBlock block={block} rows={1} />
        </BlockSection>
      );
      break;

    case 'item-grid-2-rows':
      content = (
        <BlockSection block={block} headerType={sectionHeaderType}>
          <ItemGridRowBlock block={block} rows={2} />
        </BlockSection>
      );
      break;

    case 'item-grid-3-rows':
      content = (
        <BlockSection block={block} headerType={sectionHeaderType}>
          <ItemGridRowBlock block={block} rows={3} />
        </BlockSection>
      );
      break;

    case 'item-grid-recently-viewed':
      content = (
        <RecentlyViewedItemBlock block={block} headerType={sectionHeaderType} />
      );
      break;

    case 'group-categories':
      content = (
        <BlockSection block={block} headerType={sectionHeaderType}>
          <CategorySelections />
        </BlockSection>
      );
      break;

    case 'ad-slot':
      content = (
        <div className="container my-4 md:my-8">
          <FreestarAd adSlot={block.title} />
        </div>
      );
      break;

    case 'top-models':
      content = <ModelsBlock block={block} headerType={sectionHeaderType} />;
      break;

    case 'results':
    case 'results-grid':
      content = <ResultsGridBlock />;
      break;

    case 'buying-guide':
      content = (
        <div className="container mb-4">
          <BuyingGuideBlock block={block} headerType={sectionHeaderType} />
        </div>
      );
      break;

    case 'text-pills':
      content = <TextPillsBlock block={block} headerType={sectionHeaderType} />;
      break;

    case 'short-hero-carousel':
      if (block.tiles && block.tiles.length > 0) {
        content = <ShortHeroBlock block={block} />;
      }
      break;

    default:
      if (getEnv() === 'production') {
        content = null;
      } else {
        console.warn("Didn't render unknown block:", block.layout);
        content = (
          <div className="container mb-4">
            <Message warning>
              Not setup to handle rendering &ldquo;
              <code>{block.layout}</code>
              &rdquo; layout type.
            </Message>
          </div>
        );
      }
      break;
  }

  if (getEnv() !== 'production' && block.login_required) {
    return (
      <div className="border-primary-500 bg-primary-100 container my-4 px-0 border rounded">
        <div className="text-primary-700 mb-4 ml-4 mt-2 font-semibold">
          Login required block
        </div>

        {content}
      </div>
    );
  }

  return content;
};

export default RenderBlock;
